// CreateVenue.js
import React, { useState, useEffect } from "react";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  writeBatch,
  Timestamp,
} from "firebase/firestore";
import { Input } from "./input";

import { Button } from "./button";
import { showAlert } from "../alertService";
import { useNavigate } from "react-router-dom";
import CatalogValueFields from "./CatalogValueFields";
import CatalogCheckboxGroup from "./CatalogCheckboxGroup";
import FreeformDocumentsField from "./FreeformDocumentsField";
import ArrayObjectEditor from "./ArrayObjectEditor";
import { LocationForm } from "./LocationForm";
import { loadAllCatalogs } from "../utils/catalogs";
import { useAuth } from "./AuthProvider";
import BilingualDescriptionField from "./BilingualDescriptionField";
import EventTypeTagsField from "./EventTypeTagsField";
import EspaciosManager from "./EspaciosManager";
import { calcularRollup } from "../utils/espacios";

// Utility to slugify the name
function slugify(str) {
  return str
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "") // Remove accents
    .replace(/[^a-z0-9\s-]/g, "") // Remove non-alphanumeric except spaces and dashes
    .trim()
    .replace(/\s+/g, "-") // Replace spaces with dashes
    .replace(/-+/g, "-"); // Collapse multiple dashes
}

export const CreateVenue = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [venueData, setVenueData] = useState(null);
  const [catalogs, setCatalogs] = useState(null);

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const db = getFirestore();
        const templateDoc = await getDoc(doc(db, "config", "venueTemplate"));
        if (templateDoc.exists()) {
          const data = templateDoc.data();
          setVenueData({
            ...data,
            name: "",
          });
        } else {
          console.error("Venue template not found.");
        }
      } catch (error) {
        console.error("Error fetching venue template:", error);
      }
    };
    fetchTemplate();
  }, []);

  useEffect(() => {
    async function fetchCatalogs() {
      try {
        const loaded = await loadAllCatalogs();
        setCatalogs(loaded);
      } catch (e) {
        console.error("Error fetching catalogs:", e);
        showAlert("Error al cargar los catalogos, comuníquese con el administrador.", "error");
      }
    }
    fetchCatalogs();
  }, []);

  // Handlers for nested/array fields
  const handleNestedChange = (group, name, value) => {
    setVenueData((prev) => ({
      ...prev,
      [group]: {
        ...prev[group],
        [name]: value,
      },
    }));
  };
  const handleArrayChange = (arrayName, arr) => {
    setVenueData((prev) => ({ ...prev, [arrayName]: arr }));
  };
  const handleCatalogIdsChange = (field, ids) => {
    setVenueData((prev) => ({ ...prev, [field]: ids }));
  };
  const handleCatalogValueChange = (field, key, value) => {
    setVenueData((prev) => ({
      ...prev,
      [field]: { ...prev[field], [key]: value },
    }));
  };

  const handleRecorridoChange = (name, value) => {
    setVenueData((prev) => ({
      ...prev,
      recorrido: { ...prev.recorrido, [name]: value },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!venueData.name || !venueData.name.trim()) {
      showAlert("El nombre del venue no puede estar vacío.", "error");
      return;
    }
    try {
      const db = getFirestore();
      // Read the nextId from config
      const nextIdDocRef = doc(db, "config", "nextId");
      const nextIdDoc = await getDoc(nextIdDocRef);
      let nextVenueId = 0;
      if (nextIdDoc.exists()) {
        nextVenueId = nextIdDoc.data().next || 0;
      }
      // Prepare the new venue data with venueInternalId
      const venueId = slugify(venueData.name);
      // Set timestamps in GMT-6
      const now = new Date();
      const gmt6 = new Date(now.toLocaleString("en-US", { timeZone: "America/Mexico_City" }));
      // Los espacios viven en su subcolección, nunca como campo del doc. El
      // rollup (y permiteEventosSimultaneos si no es el default) solo se
      // escribe cuando el owner capturó espacios — un venue sin espacios no
      // debe cambiar de forma.
      const { espacios: espaciosList = [], ...venueFields } = venueData;
      if (venueFields.permiteEventosSimultaneos !== false) {
        delete venueFields.permiteEventosSimultaneos;
      }
      const venueToSave = {
        ...venueFields,
        venueInternalId: nextVenueId,
        lastUpdate: Timestamp.fromDate(gmt6),
        createdAt: Timestamp.fromDate(gmt6),
      };
      if (espaciosList.length > 0) {
        const rollup = calcularRollup(espaciosList);
        Object.assign(venueToSave, rollup);
        // basicInfo["3"] derivado de capacidadMax para los filtros existentes
        if (rollup.capacidadMax != null && !Array.isArray(venueToSave.basicInfo)) {
          venueToSave.basicInfo = {
            ...(venueToSave.basicInfo || {}),
            3: String(rollup.capacidadMax),
          };
        }
      }
      await setDoc(doc(db, "venues", venueId), venueToSave);
      if (espaciosList.length > 0) {
        const batch = writeBatch(db);
        espaciosList.forEach(({ id: espacioId, ...data }) => {
          batch.set(doc(db, "venues", venueId, "espacios", espacioId), data);
        });
        await batch.commit();
      }
      // Increment the nextId in config
      await updateDoc(nextIdDocRef, { next: nextVenueId + 1 });
      showAlert("Venue Created", "success");
      navigate(`/dashboard/venues/${venueId}`);
    } catch (error) {
      console.error("Error creating venue:", error);
      showAlert("Error creating venue: " + error, "error");
    }
  };

  if (!venueData || !catalogs) {
    return <div>Loading form...</div>;
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-4xl mx-auto p-4 space-y-8 max-lg:px-0">
      <h1 className="text-2xl font-bold mb-6">Crear Nuevo Venue</h1>
      {/* General Information */}
      <fieldset className="p-4 border rounded mb-4">
        <legend className="font-semibold mb-2">Información General</legend>
        <div className="mb-4">
          <Input
            type="text"
            name="name"
            value={venueData.name}
            onChange={e => setVenueData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Nombre del venue"
          />
        </div>
        <div className="mb-4">
          <BilingualDescriptionField
            valueEs={venueData.description || ""}
            valueEn={venueData.descriptionEn || ""}
            onChangeEs={v => setVenueData(prev => ({ ...prev, description: v }))}
            onChangeEn={v => setVenueData(prev => ({ ...prev, descriptionEn: v }))}
            rows={4}
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tipos de Evento
          </label>
          <EventTypeTagsField
            value={venueData.eventTypes || []}
            onChange={(arr) =>
              setVenueData((prev) => ({ ...prev, eventTypes: arr }))
            }
          />
        </div>
        <div className="mb-4">
          <textarea
            name="address"
            value={venueData.address}
            onChange={e => setVenueData(prev => ({ ...prev, address: e.target.value }))}
            placeholder="Dirección"
            className="w-full border p-2 rounded"
          />
        </div>
      </fieldset>

      {/* Social Media */}
      <fieldset className="p-4 border rounded mb-4">
        <legend className="font-semibold mb-2">Redes Sociales</legend>
        <Input
          type="text"
          name="instagram"
          value={venueData.social?.instagram || ""}
          onChange={e => setVenueData(prev => ({ ...prev, social: { ...prev.social, instagram: e.target.value } }))}
          placeholder="Instagram"
        />
        <Input
          type="text"
          name="whatsapp"
          value={venueData.social?.whatsapp || ""}
          onChange={e => setVenueData(prev => ({ ...prev, social: { ...prev.social, whatsapp: e.target.value } }))}
          placeholder="WhatsApp"
        />
        <Input
          type="text"
          name="website"
          value={venueData.social?.website || ""}
          onChange={e => setVenueData(prev => ({ ...prev, social: { ...prev.social, website: e.target.value } }))}
          placeholder="Sitio web"
        />
      </fieldset>

      {/* Catalog-driven fields */}
      <fieldset className="p-4 border rounded mb-4">
        <legend className="font-semibold mb-2">Información básica</legend>
        <CatalogValueFields
          catalog={catalogs.basicInfo || []}
          values={venueData.basicInfo || {}}
          onChange={(key, value) => handleCatalogValueChange("basicInfo", key, value)}
          label="Información básica del venue"
        />
      </fieldset>

      {/* Servicios Incluidos */}
      <fieldset className="p-4 border rounded mb-4">
        <legend className="font-semibold mb-2">Servicios Incluidos</legend>
        <CatalogCheckboxGroup
          catalog={catalogs.servicios || []}
          selectedIds={venueData.serviciosIncluidos || []}
          onChange={ids => handleCatalogIdsChange("serviciosIncluidos", ids)}
          label="Selecciona los servicios incluidos"
        />
      </fieldset>

      {/* Servicios Opcionales */}
      <fieldset className="p-4 border rounded mb-4">
        <legend className="font-semibold mb-2">Servicios Opcionales</legend>
        <CatalogCheckboxGroup
          catalog={catalogs.servicios || []}
          selectedIds={venueData.serviciosOpcionales || []}
          onChange={ids => handleCatalogIdsChange("serviciosOpcionales", ids)}
          label="Selecciona los servicios opcionales"
        />
      </fieldset>

      {/* Servicios Exclusivos */}
      <fieldset className="p-4 border rounded mb-4">
        <legend className="font-semibold mb-2">Servicios Exclusivos</legend>
        <CatalogCheckboxGroup
          catalog={catalogs.servicios || []}
          selectedIds={venueData.serviciosExclusivos || []}
          onChange={ids => handleCatalogIdsChange("serviciosExclusivos", ids)}
          label="Selecciona los servicios exclusivos"
        />
      </fieldset>

      {/* Servicios Externos */}
      <fieldset className="p-4 border rounded mb-4">
        <legend className="font-semibold mb-2">Servicios Externos</legend>
        <CatalogCheckboxGroup
          catalog={catalogs.servicios || []}
          selectedIds={venueData.serviciosExternos || []}
          onChange={ids => handleCatalogIdsChange("serviciosExternos", ids)}
          label="Selecciona los servicios externos"
        />
      </fieldset>

      {/* Instalaciones */}
      <fieldset className="p-4 border rounded mb-4">
        <legend className="font-semibold mb-2">Instalaciones</legend>
        <CatalogCheckboxGroup
          catalog={catalogs.instalaciones || []}
          selectedIds={venueData.instalaciones || []}
          onChange={ids => handleCatalogIdsChange("instalaciones", ids)}
          label="Selecciona las instalaciones"
        />
      </fieldset>

      {/* Alimentos y Bebidas */}
      <fieldset className="p-4 border rounded mb-4">
        <legend className="font-semibold mb-2">Alimentos y Bebidas</legend>
        <CatalogCheckboxGroup
          catalog={catalogs.alimentosBebidas || []}
          selectedIds={venueData.alimentosBebidas || []}
          onChange={ids => handleCatalogIdsChange("alimentosBebidas", ids)}
          label="Selecciona alimentos y bebidas"
        />
      </fieldset>

      {/* Áreas Adicionales */}
      <fieldset className="p-4 border rounded mb-4">
        <legend className="font-semibold mb-2">Áreas Adicionales</legend>
        <CatalogCheckboxGroup
          catalog={catalogs.areasAdicionales || []}
          selectedIds={venueData.areasAdicionales || []}
          onChange={ids => handleCatalogIdsChange("areasAdicionales", ids)}
          label="Selecciona áreas adicionales"
        />
      </fieldset>

      {/* Transporte Terrestre */}
      <fieldset className="p-4 border rounded mb-4">
        <legend className="font-semibold mb-2">Transporte Terrestre</legend>
        <CatalogCheckboxGroup
          catalog={catalogs.transporteTerrestre || []}
          selectedIds={venueData.transporteTerrestre || []}
          onChange={ids => handleCatalogIdsChange("transporteTerrestre", ids)}
          label="Selecciona opciones de transporte terrestre"
        />
      </fieldset>

      {/* Estacionamiento */}
      <fieldset className="p-4 border rounded mb-4">
        <legend className="font-semibold mb-2">Estacionamiento</legend>
        <CatalogValueFields
          catalog={catalogs.estacionamiento || []}
          values={venueData.estacionamiento || {}}
          onChange={(key, value) => handleCatalogValueChange("estacionamiento", key, value)}
          label="Información de estacionamiento"
        />
      </fieldset>

      {/* Capacidad */}
      <fieldset className="p-4 border rounded mb-4">
        <legend className="font-semibold mb-2">Aforo</legend>
        <CatalogValueFields
          catalog={catalogs.capacityFields || []}
          values={venueData.capacityFields || {}}
          onChange={(key, value) => handleCatalogValueChange("capacityFields", key, value)}
          label="Capacidad por tipo (Personas)"
        />
      </fieldset>

      {/* Espacios (se escriben en la subcolección al crear el venue) */}
      <fieldset className="p-4 border rounded mb-4">
        <legend className="font-semibold mb-2">Espacios y salones</legend>
        {/* El venue aún no existe: no puede tener el flag admin, así que los
            espacios capturados en el alta nacen solo de exhibición. Un admin
            habilita multiespacio después y el owner marca cuáles se rentan. */}
        <EspaciosManager
          multiespacioActivo={false}
          venueId={null}
          storageVenueId={venueData.name ? slugify(venueData.name) : ""}
          espacios={venueData.espacios || []}
          onEspaciosChange={(arr) => handleArrayChange("espacios", arr)}
          permiteEventosSimultaneos={venueData.permiteEventosSimultaneos !== false}
          onPermiteChange={(v) =>
            setVenueData((prev) => ({ ...prev, permiteEventosSimultaneos: v }))
          }
          basicInfo={venueData.basicInfo}
          catalogs={catalogs}
        />
      </fieldset>

      {/* Política de Pagos */}
      <fieldset className="p-4 border rounded mb-4">
        <legend className="font-semibold mb-2">Pagos</legend>
        <CatalogValueFields
          catalog={catalogs.politicaPagos || []}
          values={venueData.politicaPagos || {}}
          onChange={(key, value) => handleCatalogValueChange("politicaPagos", key, value)}
          label="Política de pagos"
        />
      </fieldset>

      {/* Documentos */}
      <fieldset className="p-4 border rounded mb-4">
        <legend className="font-semibold mb-2">Documentos</legend>
        <FreeformDocumentsField
          catalog={catalogs.documentos || []}
          values={venueData.documentos}
          onChange={(arr) => handleArrayChange("documentos", arr)}
          venueId={venueData.name ? slugify(venueData.name) : ""}
          currentUser={user}
        />
      </fieldset>

      {/* Aeropuertos */}
      <fieldset className="p-4 border rounded mb-4">
        <legend className="font-semibold mb-2">Aeropuertos</legend>
        <ArrayObjectEditor
          value={venueData.aeropuertos || []}
          onChange={arr => handleArrayChange("aeropuertos", arr)}
          fields={[
            { key: "name", label: "Nombre", type: "text" },
            { key: "distance", label: "Distancia", type: "text" },
            { key: "travelTime", label: "Tiempo de viaje", type: "text" },
          ]}
          label="Aeropuertos cercanos"
        />
      </fieldset>

      {/* Hoteles */}
      <fieldset className="p-4 border rounded mb-4">
        <legend className="font-semibold mb-2">Hoteles</legend>
        <ArrayObjectEditor
          value={venueData.hoteles || []}
          onChange={arr => handleArrayChange("hoteles", arr)}
          fields={[
            { key: "name", label: "Nombre", type: "text" },
            { key: "distance", label: "Distancia", type: "text" },
            { key: "travelTime", label: "Tiempo de viaje", type: "text" },
            { key: "url", label: "URL", type: "url" },
          ]}
          label="Hoteles cercanos"
        />
      </fieldset>

      {/* Localización */}
      <fieldset className="p-4 border rounded mb-4">
        <legend className="font-semibold mb-2">Localización</legend>
        <LocationForm
          location={venueData.location || {}}
          onChange={(name, value) => handleNestedChange("location", name, value)}
          venueId={venueData.name ? slugify(venueData.name) : ""}
        />
      </fieldset>

      {/* Recorrido */}
      <fieldset className="p-4 border rounded mb-4">
        <legend className="font-semibold mb-2">Recorrido</legend>
        <Input
          type="text"
          name="recorridoTipo"
          value={venueData.recorrido?.tipo || ""}
          onChange={e => handleRecorridoChange("tipo", e.target.value)}
          placeholder="Tipo de recorrido (ej: matterport)"
        />
        <Input
          type="text"
          name="recorridoId"
          value={venueData.recorrido?.id || ""}
          onChange={e => handleRecorridoChange("id", e.target.value)}
          placeholder="ID de recorrido (ej: ZemqbJEApKb)"
        />
      </fieldset>

      <Button type="submit" className="w-full mt-4">
        Crear Venue
      </Button>
    </form>
  );
};

export default CreateVenue;
